import { useMemo, useState } from "react"
import { useParams } from "react-router"
import { generatePath, useNavigate } from "react-router-dom"
import { observer } from "mobx-react"
import { Button, List, Tag } from "antd"
import {
  CopyOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from "@ant-design/icons"
import Tippy from "@tippyjs/react"

import { Navigation } from "@src/app/components/navigation"
import { notify } from "@src/app/components/notify"
import { paths } from "@src/app/paths"
import { dateTimeFormat } from "@src/shared/date"
import { paddingLimit } from "@src/shared/strings"
import css from "./index.module.css"
import { ProjectStore } from "./store"

const { Item } = List

export const ProjectPage = observer(() => {
  const { id } = useParams()

  const [store] = useState(new ProjectStore(id!))

  const [isCallbackUrlHidden, setIsCallbackUrlHidden] = useState(true)

  const { isLoading, project } = store

  const navigate = useNavigate()

  const {
    callback_signature_key,
    callback_url,
    created_at,
    description,
    enabled_crypto,
    hosted,
    id: projectId,
    image,
    logo,
    name,
    status,
  } = project || {}

  const icon = useMemo(
    () => (isCallbackUrlHidden ? <EyeOutlined /> : <EyeInvisibleOutlined />),
    [isCallbackUrlHidden],
  )

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text)

    notify.success("Copied")
  }

  return (
    <div>
      <Navigation title="Project details" />
      <List className={css.list} loading={isLoading}>
        <Item>
          <div></div>
          <Button
            icon={<EditOutlined />}
            onClick={() =>
              navigate(generatePath(paths.projectUpsert, { id: id! }))
            }
            title="Edit Project"
          />
        </Item>
        <Item>
          <div>ID</div>
          <div className={css.wrapper}>
            <div className={css.listValue}>{projectId}</div>
            <Tippy className={css.tooltip} content="Copy to clipboard">
              <Button
                icon={<CopyOutlined />}
                onClick={() => copyText(projectId!)}
              />
            </Tippy>
          </div>
        </Item>
        <Item>
          <div>Name</div>
          <div className={css.listValue}>{name}</div>
        </Item>
        <Item>
          <div>Description</div>
          <div className={css.listValue}>{description}</div>
        </Item>
        <Item>
          <div>Status</div>
          <div className={css.listValue}>{status}</div>
        </Item>
        <Item>
          <div>Created</div>
          <div className={css.listValue}>
            {created_at && dateTimeFormat(created_at)}
          </div>
        </Item>
        {enabled_crypto && (
          <Item>
            <div>Enabled Crypto</div>
            <div className={css.listValue}>
              {enabled_crypto.length > 0 && (
                <div className={css.container}>
                  {(enabled_crypto as any[]).map((crypto, i: number) => (
                    <Tag key={i}>
                      {crypto.symbol} ({crypto.network})
                    </Tag>
                  ))}
                </div>
              )}
            </div>
          </Item>
        )}
        {callback_url && (
          <Item>
            <div>Callback URL</div>
            <div className={css.listValue}>{callback_url}</div>
          </Item>
        )}
        {hosted?.return_url && (
          <Item>
            <div>Return URL</div>
            <div className={css.listValue}>{hosted?.return_url}</div>
          </Item>
        )}
        {callback_signature_key && (
          <Item>
            <div>Callback Signature Key</div>
            <div className={css.wrapper}>
              <div className={css.listValue}>
                {isCallbackUrlHidden
                  ? paddingLimit(callback_signature_key!, 0, 8)
                  : callback_signature_key}
              </div>
              <Button
                icon={icon}
                onClick={() => setIsCallbackUrlHidden(!isCallbackUrlHidden)}
              />
              <Tippy className={css.tooltip} content="Copy to clipboard">
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => copyText(callback_signature_key)}
                />
              </Tippy>
            </div>
          </Item>
        )}
        {/* @ts-ignore */}
        {logo?.versions?.original && (
          <Item>
            <div>Project logo</div>
            <div className={css.imageWrapper}>
              <img
                alt="Project logo"
                className={css.image}
                // @ts-ignore
                src={logo?.versions?.original}
              />
            </div>
          </Item>
        )}
        {/* @ts-ignore */}
        {image?.versions?.original && (
          <Item>
            <div>Project image</div>
            <div className={css.imageWrapper}>
              <img
                alt="Project image"
                className={css.image}
                // @ts-ignore
                src={image?.versions?.original}
              />
            </div>
          </Item>
        )}
        <Item>
          <div>Theme</div>
          <div className={css.listValue}>{hosted?.theme}</div>
        </Item>
      </List>
    </div>
  )
})
